import merge from 'lodash/merge';
import { combineReducers } from 'redux';
import { normalize } from 'normalizr';

import { postSchema } from '../../schemas';
import {
  GET_POST_COMMENTS_FULFILLED,
  GET_POST_COMMENTS_PENDING,
  GET_POST_COMMENTS_REJECTED,
  GET_POST_INFO_FULFILLED,
  GET_POST_INFO_PENDING,
  GET_POST_INFO_REJECTED,
} from '../../types';

// State interfaces
interface InfoState {
  id: string | null;
  entities: any;
  isFetching: boolean;
  error: any | null;
}

interface CommentsState {
  post: string | null;
  data: any[];
  total?: number;
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface PostState {
  info: InfoState;
  comments: CommentsState;
}

// Action interfaces
interface GetPostInfoPendingAction {
  type: typeof GET_POST_INFO_PENDING;
}

interface GetPostInfoFulfilledAction {
  type: typeof GET_POST_INFO_FULFILLED;
  payload: {
    data: any;
  };
}

interface GetPostInfoRejectedAction {
  type: typeof GET_POST_INFO_REJECTED;
  error: any;
}

interface GetPostCommentsPendingAction {
  type: typeof GET_POST_COMMENTS_PENDING;
  meta: {
    post: string;
  };
}

interface GetPostCommentsFulfilledAction {
  type: typeof GET_POST_COMMENTS_FULFILLED;
  payload: {
    data: {
      content: any[];
      total: number;
    };
  };
  meta: {
    post: string;
    offset: number;
    limit: number;
  };
}

interface GetPostCommentsRejectedAction {
  type: typeof GET_POST_COMMENTS_REJECTED;
  error: any;
}

type InfoAction = GetPostInfoPendingAction | GetPostInfoFulfilledAction | GetPostInfoRejectedAction;
type CommentsAction = GetPostCommentsPendingAction | GetPostCommentsFulfilledAction | GetPostCommentsRejectedAction;

const INITIAL_STATE = {
  INFO: {
    id: null,
    entities: {},
    isFetching: false,
    error: null,
  },
  COMMENTS: {
    post: null,
    data: [],
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
};

const info = (state: InfoState = INITIAL_STATE.INFO, action: InfoAction): InfoState => {
  switch (action.type) {
    case GET_POST_INFO_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_POST_INFO_FULFILLED: {
      const {
        data,
      } = action.payload;
      if (!data) {
        return INITIAL_STATE.INFO;
      }
      const {
        entities,
        result,
      } = normalize(data, postSchema);
      return {
        entities: merge(state.entities, entities),
        id: result,
        isFetching: false,
        error: null,
      };
    }
    case GET_POST_INFO_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const comments = (state: CommentsState = INITIAL_STATE.COMMENTS, action: CommentsAction): CommentsState => {
  switch (action.type) {
    case GET_POST_COMMENTS_PENDING:
      if (!state.post || action.meta.post !== state.post) {
        return {
          ...INITIAL_STATE.COMMENTS,
          isFetching: true,
        };
      }
      return {
        ...state,
        isFetching: true,
      };
    case GET_POST_COMMENTS_FULFILLED: {
      const {
        post,
        offset,
        limit,
      } = action.meta;

      const {
        data: {
          content,
          total,
        },
      } = action.payload;

      let data = content;

      if (post === state.post && offset > state.offset) {
        data = state.data.concat(data);
      }

      return {
        post,
        data,
        total,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_POST_COMMENTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

export default combineReducers<PostState>({
  info,
  comments,
});